import React, { useState, useEffect, useCallback } from 'react';
import { Users, Briefcase, Calendar, Mail, TrendingUp, Clock, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [applications, setApplications] = useState([]);
  const [siteVisits, setSiteVisits] = useState([]);
  const [activeTab, setActiveTab] = useState('inquiries');
  const [loading, setLoading] = useState(true);

  const refreshData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, inquiriesRes, applicationsRes, siteVisitsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/stats`),
        axios.get(`${BACKEND_URL}/api/admin/inquiries`),
        axios.get(`${BACKEND_URL}/api/admin/applications`),
        axios.get(`${BACKEND_URL}/api/admin/site-visits`)
      ]);

      setStats(statsRes.data);
      setInquiries(inquiriesRes.data);
      setApplications(applicationsRes.data);
      setSiteVisits(siteVisitsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const updateStatus = async (type, id, status) => {
    try {
      const endpoint = type === 'inquiry' ? 'inquiries' : 'applications';
      await axios.patch(`${BACKEND_URL}/api/admin/${endpoint}/${id}/status`, null, {
        params: { status }
      });
      toast.success('Status updated successfully');
      refreshData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Manage inquiries, applications, and site visits</p>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <Mail className="text-blue-600" size={32} />
              <span className="text-sm text-gray-600">Total</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{stats?.total_inquiries || 0}</div>
            <div className="text-sm text-gray-600">Contact Inquiries</div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <Briefcase className="text-green-600" size={32} />
              <span className="text-sm text-gray-600">Pending</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{stats?.pending_applications || 0}</div>
            <div className="text-sm text-gray-600">Job Applications</div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <Calendar className="text-amber-600" size={32} />
              <span className="text-sm text-gray-600">Scheduled</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{stats?.scheduled_visits || 0}</div>
            <div className="text-sm text-gray-600">Site Visits</div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <Users className="text-purple-600" size={32} />
              <span className="text-sm text-gray-600">Active</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{stats?.newsletter_subscribers || 0}</div>
            <div className="text-sm text-gray-600">Subscribers</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('inquiries')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'inquiries'
                    ? 'border-amber-600 text-amber-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Contact Inquiries ({inquiries.length})
              </button>
              <button
                onClick={() => setActiveTab('applications')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'applications'
                    ? 'border-amber-600 text-amber-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Job Applications ({applications.length})
              </button>
              <button
                onClick={() => setActiveTab('visits')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'visits'
                    ? 'border-amber-600 text-amber-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Site Visits ({siteVisits.length})
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Contact Inquiries */}
            {activeTab === 'inquiries' && (
              <div className="space-y-4">
                {inquiries.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No inquiries yet</p>
                ) : (
                  inquiries.map((inquiry) => (
                    <div key={inquiry.id} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">{inquiry.name}</h3>
                          <p className="text-sm text-gray-600">{inquiry.email} • {inquiry.phone}</p>
                        </div>
                        <select
                          value={inquiry.status}
                          onChange={(e) => updateStatus('inquiry', inquiry.id, e.target.value)}
                          className="px-3 py-1 rounded-lg border border-gray-300 text-sm"
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                      {inquiry.project && <p className="text-sm text-gray-700 mb-2"><strong>Project:</strong> {inquiry.project}</p>}
                      <p className="text-gray-700">{inquiry.message}</p>
                      <p className="text-xs text-gray-500 mt-3">
                        {new Date(inquiry.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Job Applications */}
            {activeTab === 'applications' && (
              <div className="space-y-4">
                {applications.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No applications yet</p>
                ) : (
                  applications.map((app) => (
                    <div key={app.id} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">{app.name}</h3>
                          <p className="text-sm text-gray-600">{app.email} • {app.phone}</p>
                        </div>
                        <select
                          value={app.status}
                          onChange={(e) => updateStatus('application', app.id, e.target.value)}
                          className="px-3 py-1 rounded-lg border border-gray-300 text-sm"
                        >
                          <option value="pending">Pending</option>
                          <option value="reviewing">Reviewing</option>
                          <option value="shortlisted">Shortlisted</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </div>
                      <p className="text-sm text-gray-700 mb-2"><strong>Position:</strong> {app.position}</p>
                      <p className="text-sm text-gray-700 mb-2"><strong>Experience:</strong> {app.experience}</p>
                      {app.resume_filename && (
                        <p className="text-sm text-gray-700 mb-2"><strong>Resume:</strong> {app.resume_filename}</p>
                      )}
                      {app.cover_letter && <p className="text-gray-700 mt-3">{app.cover_letter}</p>}
                      <p className="text-xs text-gray-500 mt-3">
                        {new Date(app.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Site Visits */}
            {activeTab === 'visits' && (
              <div className="space-y-4">
                {siteVisits.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No site visits scheduled</p>
                ) : (
                  siteVisits.map((visit) => (
                    <div key={visit.id} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">{visit.name}</h3>
                          <p className="text-sm text-gray-600">{visit.email} • {visit.phone}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          (() => {
                            if (visit.status === 'pending') return 'bg-yellow-100 text-yellow-800';
                            if (visit.status === 'confirmed') return 'bg-green-100 text-green-800';
                            if (visit.status === 'completed') return 'bg-blue-100 text-blue-800';
                            return 'bg-gray-100 text-gray-800';
                          })()
                        }`}>
                          {visit.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mb-2"><strong>Project:</strong> {visit.project_name}</p>
                      {visit.message && <p className="text-gray-700">{visit.message}</p>}
                      <p className="text-xs text-gray-500 mt-3">
                        Requested: {new Date(visit.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
